import axios from "axios";
import { isCacheableGet, saveCache, readCache, enqueue, isOnline, clientId, saveLocalTimer } from "@/lib/offline";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

// In-memory token cache — fallback when localStorage is unavailable
// (iOS Safari Private Mode, strict-tracking browsers, iframes met partitioned storage).
let _memToken = null;
export function setAuthToken(token, remember = true) {
  _memToken = token || null;
  // Kies persistente (localStorage — 30d, remember=true) of sessie-scoped (sessionStorage — tab close)
  const primary = remember ? "localStorage" : "sessionStorage";
  const secondary = remember ? "sessionStorage" : "localStorage";
  try {
    if (token) {
      window[primary].setItem("pb_token", token);
      // Ruim de andere op zodat er geen stale token in de fallback blijft
      try { window[secondary].removeItem("pb_token"); } catch (_) { /* noop */ }
    } else {
      window.localStorage.removeItem("pb_token");
      window.sessionStorage.removeItem("pb_token");
    }
  } catch (_) { /* private mode: keep in memory only */ }
}
export function getAuthToken() {
  if (_memToken) return _memToken;
  try {
    const t = window.sessionStorage.getItem("pb_token") || window.localStorage.getItem("pb_token");
    if (t) _memToken = t;
    return t;
  } catch (_) { return _memToken; }
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15000,
});

// Public API (no cookies, no auth interceptor) — for marketing site / pricing
export const publicApi = axios.create({
  baseURL: API_BASE,
});

// Attach token from storage/memory as fallback (cookies may not survive cross-site iframes / iOS ITP)
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // If we are offline, short-circuit certain POSTs into the queue.
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  if (!isOnline() && method === "post" && (url.endsWith("/time-entries/start") || url.endsWith("/time-entries/stop") || url.endsWith("/time-entries/pause"))) {
    const isStart = url.endsWith("/time-entries/start");
    const isPause = url.endsWith("/time-entries/pause");
    const body = config.data ? (typeof config.data === "string" ? JSON.parse(config.data) : config.data) : {};
    const cid = body.client_id || clientId();
    const nowIso = new Date().toISOString();
    let queued;
    if (isStart) {
      queued = { method: "POST", url, body: { assignment_id: body.assignment_id, client_id: cid, client_start_at: nowIso } };
    } else if (isPause) {
      queued = { method: "POST", url, body: { client_id: cid, action: body.action, client_at: nowIso } };
    } else {
      queued = { method: "POST", url, body: { client_id: cid, client_stop_at: nowIso, break_minutes: body.break_minutes, client_break_minutes: body.client_break_minutes } };
    }
    enqueue(queued);
    // Optimistic local response
    let synthetic;
    if (isStart) {
      synthetic = { id: `offline_${cid}`, assignment_id: body.assignment_id, start_at: nowIso, stop_at: null, client_id: cid, break_minutes: 0, is_paused: false, _offline: true };
    } else if (isPause) {
      synthetic = { id: `offline_pause_${cid}`, action: body.action, client_id: cid, _offline: true };
    } else {
      synthetic = { id: `offline_stop_${cid}`, stop_at: nowIso, client_id: cid, break_minutes: body.break_minutes || 0, _offline: true };
    }
    if (isStart) saveLocalTimer(synthetic);
    else if (!isPause) saveLocalTimer(null);
    // Throw a special error so caller can handle "queued offline"
    const err = new Error("OFFLINE_QUEUED");
    err.offline = true;
    err.synthetic = synthetic;
    return Promise.reject(err);
  }
  return config;
});

// Cache successful GET responses for offline replay.
api.interceptors.response.use(
  (r) => {
    try {
      if ((r.config.method || "get").toLowerCase() === "get" && isCacheableGet(r.config.url)) {
        const fullUrl = r.config.url + (r.config.params ? `?${new URLSearchParams(r.config.params).toString()}` : "");
        saveCache("GET", fullUrl, r.data);
      }
    } catch (_) { /* ignore */ }
    return r;
  },
  (err) => {
    // Offline-queued short-circuit propagates as-is (marked with .offline)
    if (err && err.offline) return Promise.reject(err);
    // Paywall
    if (err?.response?.status === 402) {
      try { sessionStorage.setItem("pb_paywall", "1"); } catch (_) { /* storage disabled */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/billing")) {
        window.location.href = "/billing?paywall=1";
      }
      return Promise.reject(err);
    }
    // Network failure on a cacheable GET → return cached response if available.
    const noResponse = !err?.response;
    const cfg = err?.config || {};
    const method = (cfg.method || "get").toLowerCase();
    if (noResponse && method === "get" && isCacheableGet(cfg.url)) {
      const fullUrl = cfg.url + (cfg.params ? `?${new URLSearchParams(cfg.params).toString()}` : "");
      const cached = readCache("GET", fullUrl);
      if (cached) {
        return Promise.resolve({ data: cached.data, status: 200, statusText: "OK (offline-cache)", headers: {}, config: cfg, _fromCache: true, _cachedAt: cached.savedAt });
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
